import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, Globe, ArrowLeft, ChevronRight } from 'lucide-react';
import SEO from './SEO';
import CategoryNav from './CategoryNav';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { HOME_SERVICES_ENTRIES, type HomeServiceEntry } from './HomeServices';
import SocialLinks from './SocialLinks';

const BASE_URL = 'https://dallasdesiscoop.com';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=1200&h=800&fit=crop';

// Custom About copy for specific listings; everything else uses the default template.
const CUSTOM_ABOUT: Record<string, string> = {
  'Devi Woodworks':
    'Welcome to Devi Woodworks, your trusted destination for custom closets, cabinetry, and handcrafted woodwork in the DFW area. We specialize in creating beautiful, functional, and personalized spaces, from luxury walk-in closets and custom built-ins to unique furniture and handcrafted home decor. Combining quality craftsmanship with thoughtful design, we bring your vision to life with attention to every detail. Helping individual families to real estate groups, we got you covered.',
};

// Same slug derivation the category page uses (slugify of name), exported here
// so card links and the detail page always agree — including admin-added listings.
export function homeServiceSlug(name: string) {
  return (name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function mapsUrl(e: HomeServiceEntry) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${e.name}, ${e.address || ''}`)}`;
}

export default function HomeServiceDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const mc = useManagedCollection<any>('home_services', HOME_SERVICES_ENTRIES, (e, i) =>
    slugify(e.name || String(i))
  );

  const entry: HomeServiceEntry | undefined = mc.items.find(
    (e: any) => homeServiceSlug(e.name) === slug
  );

  if (mc.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#082f6d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <SEO
          title="Home Service Not Found | Dallas Desi Scoop"
          description="This home service listing could not be found."
          canonical="/home-services"
          noindex
        />
        <p className="text-gray-500 text-lg mb-4">Home service listing not found.</p>
        <Link
          to="/home-services"
          className="text-[#082f6d] font-semibold hover:underline"
        >
          ← Back to Home Services
        </Link>
      </div>
    );
  }

  const city = entry.address?.split(',').slice(-2, -1)[0]?.trim() || 'DFW';
  const title = `${entry.name} in ${city}, TX | Home Service Professional | Dallas Desi Scoop`;
  const description =
    CUSTOM_ABOUT[entry.name] ||
    `${entry.name} is a home service professional serving the South Asian community in ${city}, TX. Find contact details, services offered, and booking info.`;
  const canonicalPath = `/home-services/${slug}`;

  // Split the stored address into street + locality for structured data.
  const addressParts = (entry.address || '').split(',').map((p) => p.trim()).filter(Boolean);
  const streetAddress =
    addressParts.length >= 3 ? addressParts.slice(0, -2).join(', ') : undefined;
  const addressLocality =
    addressParts.length >= 2 ? addressParts[addressParts.length - 2] : city;

  const jsonLd = [
    {
      '@type': 'LocalBusiness',
      name: entry.name,
      image: entry.image || FALLBACK_IMAGE,
      description: CUSTOM_ABOUT[entry.name] || entry.description || 'Home service professional serving Dallas-Fort Worth',
      address: {
        '@type': 'PostalAddress',
        streetAddress: streetAddress,
        addressLocality: addressLocality || undefined,
        addressRegion: 'TX',
        addressCountry: 'US',
      },
      telephone: entry.phone || undefined,
      url: entry.website || `${BASE_URL}${canonicalPath}`,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Home Services', item: `${BASE_URL}/home-services` },
        { '@type': 'ListItem', position: 3, name: entry.name, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <SEO
        title={title}
        description={description}
        canonical={canonicalPath}
        ogImage={entry.image || FALLBACK_IMAGE}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '260px' }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${entry.image || FALLBACK_IMAGE}")` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(8,47,109,0.75) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.88) 100%)',
          }}
        />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="self-start flex items-center gap-1.5 mb-5"
          >
            <Link
              to="/home-services"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Home Services
            </Link>
            <ChevronRight className="w-3 h-3 opacity-60 text-white/60" />
            <span className="text-white/60 text-[12px] font-medium">{entry.name}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-[26px] sm:text-[36px] font-extrabold leading-tight tracking-tight"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {entry.name}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18 }}
            className="flex flex-wrap items-center gap-3 mt-3"
          >
            {entry.address && (
              <span className="flex items-center gap-1 text-white/85 text-[13px]">
                <MapPin className="w-3.5 h-3.5 text-[#51ba4f]" />
                {entry.address}
              </span>
            )}
            {entry.description && (
              <>
                <span className="text-white/40">·</span>
                <span className="text-white/75 text-[13px]">{entry.description}</span>
              </>
            )}
          </motion.div>
        </div>
      </section>

      <CategoryNav />

      {/* Main content */}
      <div className="max-w-[1000px] mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-[18px] font-bold text-gray-900 mb-3">About {entry.name}</h2>
          <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
            {CUSTOM_ABOUT[entry.name] || (
              <>
                {entry.description
                  ? `${entry.name} offers ${entry.description.toLowerCase()}`
                  : `${entry.name} is a home service professional serving the South Asian community across Dallas–Fort Worth`}
                {city !== 'DFW' ? `, located in ${city}, TX` : ''}.
                {' '}Trusted by families across DFW for reliable, professional home service work.
                {entry.address ? ` Located at ${entry.address}.` : ''}
              </>
            )}
          </p>
          <h2 className="text-[18px] font-bold text-gray-900 mt-8 mb-3">Services & Booking</h2>
          <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
            Looking for a trusted South Asian home service professional in {city}? {entry.name} serves families across DFW — contact them directly to discuss your project and get a quote.
          </p>
          <div className="mt-6 max-w-xs">
            <SocialLinks item={{ ...entry, website: undefined }} variant="bar" />
          </div>
        </div>

        <div
          className="bg-white rounded-xl border border-gray-100 p-5 h-fit"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-gray-400 mb-4">
            Contact & Info
          </h3>
          <div className="space-y-3 text-[13px] text-gray-700">
            {entry.address && (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>{entry.address}</span>
              </div>
            )}
            {entry.phone && (
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <a href={`tel:${entry.phone.replace(/[^\d+]/g, '')}`} className="hover:underline">
                  {entry.phone}
                </a>
              </div>
            )}
            {entry.website && (
              <div className="flex items-start gap-2">
                <Globe className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <a
                  href={entry.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline break-all"
                >
                  Visit Website
                </a>
              </div>
            )}
            {!entry.address && !entry.phone && !entry.website && (
              <span className="text-gray-400">[Coming soon]</span>
            )}
          </div>
          {entry.address && !entry.hideViewLocation && (
            <a
              href={mapsUrl(entry)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 block text-center bg-[#082f6d] hover:bg-[#062454] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
            >
              Get Directions
            </a>
          )}
          <Link
            to="/home-services"
            className="mt-3 block text-center text-[#082f6d] text-[12px] font-semibold hover:underline"
          >
            Browse all home services
          </Link>
        </div>
      </div>
    </div>
  );
}
